// src/pages/admin/contact/ContactDetail.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/admin/AdminLayout";
import api from "../../../api/axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useTranslation } from "react-i18next";
import { NATIONALITY_OPTIONS } from "../../../contants/nationalities";

/** 서비스/직무 코드 → i18n 키 매핑 */
const SERVICE_LABEL_MAP = {
  CRUISE: "contact.services.cruise",
  KFOOD: "contact.services.kfood",
  KOREAEMPLOYMENT: "contact.services.koreaemployment",
};

const JOB_LABEL_MAP = {
  FNB: "contact.jobs.fnb",
  BAR: "contact.jobs.bar",
  GALLEY: "contact.jobs.galley",
  HK: "contact.jobs.hk",
  RECEPTION: "contact.jobs.reception",
  ENGINEER: "contact.jobs.engineer",
  SECURITY: "contact.jobs.security",
  ROOMSERVICE: "contact.jobs.roomservice",
};

export default function ContactDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();

  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef(null);

  // 🔹 관리자 메모 상태
  const [memo, setMemo] = useState("");
  const [savingMemo, setSavingMemo] = useState(false);
  const [memoMessage, setMemoMessage] = useState("");

  const fmtDateTime = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    const ss = String(dt.getSeconds()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd} ${hh}:${mi}:${ss}`;
  };

  // JSON 문자열 파싱
  const safeParse = (jsonStr, fallback = []) => {
    try {
      if (!jsonStr) return fallback;
      const v = typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
      return Array.isArray(v) ? v : fallback;
    } catch {
      return fallback;
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/api/contact/inquiry/${id}`);
        const d = res.data?.data ?? null;
        setData(d);
        setMemo(d?.adminMemo || "");
      } catch (e) {
        console.error(e);
        alert("상세를 불러오지 못했습니다.");
        nav("/admin/contact/list");
      }
    })();
  }, [id, nav]);

  const edu = useMemo(() => safeParse(data?.educationJson), [data]);
  const langs = useMemo(() => safeParse(data?.langTestsJson), [data]);
  const licenses = useMemo(() => safeParse(data?.licensesJson), [data]);

  // 경력 요약 문자열(멀티라인)을 표용 객체 배열로 파싱
  const expRows = useMemo(() => {
    if (!data?.experience) return [];
    return data.experience
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split(" / ");
        const company = parts[0] || "";
        const position = parts[1] || "";
        const period = parts[2] || "";
        return { company, position, period };
      });
  }, [data]);

  // ✅ 섹션 단위 캡처 + 페이지 나누기
  const onExportPdf = async () => {
    if (!printRef.current || !data) return;

    document.documentElement.classList.add("pdf-mode");
    setDownloading(true);

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableWidth = pageWidth - margin * 2;
      const bottomLimit = pageHeight - margin;

      // 🔹 pdf-section 중에서 실제로 화면에 보이는 것만 캡처
      const sections = Array.from(
        printRef.current.querySelectorAll(".pdf-section")
      ).filter((el) => {
        const style = window.getComputedStyle(el);
        const visible =
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          el.offsetWidth > 0 &&
          el.offsetHeight > 0;
        return visible;
      });

      let cursorY = margin;

      for (const section of sections) {
        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          logging: false,
          windowWidth: section.scrollWidth || undefined,
        });

        // 혹시라도 0x0 캔버스면 스킵
        if (!canvas.width || !canvas.height) continue;

        const imgData = canvas.toDataURL("image/png");
        const imgHeight = (canvas.height * usableWidth) / canvas.width;

        if (cursorY + imgHeight > bottomLimit) {
          pdf.addPage();
          cursorY = margin;
        }

        pdf.addImage(imgData, "PNG", margin, cursorY, usableWidth, imgHeight);
        cursorY += imgHeight + 4;
      }

      pdf.save(`contact_${data?.id || id}.pdf`);
    } catch (e) {
      console.error(e);
      alert("PDF 생성 중 오류가 발생했습니다.");
    } finally {
      document.documentElement.classList.remove("pdf-mode");
      setDownloading(false);
    }
  };

  // URL에서 파일명만 추출
  const getFileNameFromUrl = (url) => {
    if (!url) return "";
    try {
      const u = new URL(url);
      const path = u.pathname;
      const lastSeg = path.split("/").pop();
      return decodeURIComponent(lastSeg || "");
    } catch {
      const noQuery = url.split("?")[0];
      const lastSeg = noQuery.split("/").pop();
      try {
        return decodeURIComponent(lastSeg || "");
      } catch {
        return lastSeg || "";
      }
    }
  };

  // 🔹 관리자 메모 저장
  const onSaveMemo = async () => {
    setMemoMessage("");
    try {
      setSavingMemo(true);
      const res = await api.patch(`/api/contact/inquiry/${id}/memo`, {
        adminMemo: memo,
      });

      const updated = res.data?.data;
      if (updated) {
        setData((prev) => ({ ...(prev || {}), adminMemo: updated.adminMemo }));
      } else {
        setData((prev) => ({ ...(prev || {}), adminMemo: memo }));
      }

      setMemoMessage("메모가 저장되었습니다.");
    } catch (e) {
      console.error(e);
      setMemoMessage(
        e.response?.data?.resultMessage || "메모 저장 중 오류가 발생했습니다."
      );
    } finally {
      setSavingMemo(false);
    }
  };

  const Row = ({ label, children }) => (
    <div className="grid grid-cols-12 gap-3 border-t px-4 py-3 text-sm">
      <div className="col-span-12 font-semibold text-slate-700 md:col-span-3">
        {label}
      </div>
      <div className="col-span-12 text-slate-800 md:col-span-9">{children}</div>
    </div>
  );

  // ✅ 사전질문용: 질문 영역 넓게(10), 답변 영역 좁게(2)
  const QARow = ({ label, children }) => (
    <div className="grid grid-cols-12 gap-3 border-t px-4 py-3 text-sm">
      <div className="col-span-12 font-semibold text-slate-700 md:col-span-10">
        {label}
      </div>
      <div className="col-span-12 text-slate-800 md:col-span-2 md:text-center">
        {children}
      </div>
    </div>
  );

  if (!data) {
    return (
      <AdminLayout>
        <div className="rounded-2xl border bg-white p-8 text-center text-slate-500">
          불러오는 중...
        </div>
      </AdminLayout>
    );
  }

  /* ==== 라벨 변환 헬퍼들 ==== */

  const getServiceLabel = (code) => {
    if (!code) return "-";
    const key = SERVICE_LABEL_MAP[code];
    return key ? t(key) : code;
  };

  const getJobLabel = (code) => {
    if (!code) return "-";
    const key = JOB_LABEL_MAP[code];
    return key ? t(key) : code;
  };

  const genderLabel =
    data.gender === "M"
      ? t("contact.gender.male")
      : data.gender === "F"
      ? t("contact.gender.female")
      : data.gender || "-";

  const getNationalityLabel = (value) => {
    if (!value) return "-";
    const found = NATIONALITY_OPTIONS.find((n) => n.code === value);
    // 코드에 매칭되면 라벨, 아니면 그대로(기타 입력값)
    return found ? found.label : value;
  };

  const nationalityLabel = getNationalityLabel(data.nationality);

  /* 한국 내 체류 상태 텍스트 */
  const livingText = data.livingInKorea
    ? "한국 내 거주 중"
    : data.visaType
    ? `한국 외 거주 (비자 타입: ${data.visaType})`
    : "한국 외 거주";

  return (
    <AdminLayout>
      {/* 화면 상단 관리자 헤더 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight text-blue-900 md:text-3xl">
          온라인 지원서 상세
        </h1>
        <div className="flex gap-2">
          <button
            onClick={onExportPdf}
            disabled={downloading}
            className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {downloading ? "PDF 생성 중..." : "PDF로 저장"}
          </button>
          <Link
            to="/admin/contact/list"
            className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            목록으로
          </Link>
        </div>
      </div>

      {/* ===== PDF 캡처 타깃 (이력서 레이아웃) ===== */}
      <div ref={printRef} className="space-y-6">
        {/* ✅ 0. 최상단 회사 로고만 (서류 좌상단 로고 느낌) */}
        <div className="pdf-section pb-2">
          <img
            src="/assets/brand/logo.png"
            alt="회사 로고"
            className="h-6 object-contain"
          />
        </div>

        {/* 1. 지원서 헤더 (이름 / 지원 포지션 / 접수일 / 증명사진) */}
        <div className="pdf-section rounded-2xl border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* 왼쪽: 기본 정보만 */}
            <div className="space-y-1">
              <div className="text-xs text-slate-500">지원서 번호</div>
              <div className="text-sm font-semibold text-slate-800">
                #{data.id}
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900">
                {data.name}
              </h2>
              <div className="text-sm font-medium text-slate-700">
                {getServiceLabel(data.service)} · {getJobLabel(data.job)}
              </div>
              <div className="pt-1 text-xs text-slate-500">
                접수일 : {fmtDateTime(data.createdAt)}
              </div>
            </div>

            {/* 오른쪽: 증명사진 */}
            {data.idPhotoUrl && (
              <div className="flex justify-end">
                <img
                  src={data.idPhotoUrl}
                  crossOrigin="anonymous"
                  alt="증명사진"
                  className="h-32 w-28 rounded-lg border object-cover"
                />
              </div>
            )}
          </div>
        </div>

        {/* 2. 인적 사항 */}
        <div className="pdf-section overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="border-b px-4 py-3 text-lg font-bold text-blue-900">
            인적 사항
          </div>
          <Row label="성명">{data.name}</Row>
          <Row label="성별">{genderLabel}</Row>
          <Row label="생년월일">{data.birthDate?.slice(0, 10) || "-"}</Row>
          <Row label="국적">{nationalityLabel}</Row>
          <Row label="거주지">{data.address || "-"}</Row>
          <Row label="한국 내 체류 상태">{livingText}</Row>
          <Row label="비자 타입">{data.visaType || "-"}</Row>
          <Row label="이메일">{data.email}</Row>
          <Row label="전화번호">{data.phone}</Row>
        </div>

        {/* 3. 경력 사항 (표 형태) */}
        <div className="pdf-section overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="border-b px-4 py-3 text-lg font-bold text-blue-900">
            경력 사항
          </div>
          <div className="px-4 py-3 text-sm">
            {expRows.length === 0 ? (
              "-"
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="border px-2 py-1 text-left font-semibold">
                        회사명
                      </th>
                      <th className="border px-2 py-1 text-left font-semibold">
                        직무
                      </th>
                      <th className="border px-2 py-1 text-left font-semibold">
                        기간
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {expRows.map((row, i) => (
                      <tr key={i}>
                        <td className="border px-2 py-1 align-top">
                          {row.company || "-"}
                        </td>
                        <td className="border px-2 py-1 align-top">
                          {row.position || "-"}
                        </td>
                        <td className="border px-2 py-1 align-top">
                          {row.period || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* 4. 학력 / 어학 능력 / 자격·면허 */}
        <div className="pdf-section overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="border-b px-4 py-3 text-lg font-bold text-blue-900">
            학력 · 어학 능력 · 자격·면허
          </div>

          {/* 학력: 표 형태 */}
          <Row label="학력">
            {edu.length === 0 ? (
              "-"
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="border px-2 py-1 text-left font-semibold">
                        구분
                      </th>
                      <th className="border px-2 py-1 text-left font-semibold">
                        학교명
                      </th>
                      <th className="border px-2 py-1 text-left font-semibold">
                        전공
                      </th>
                      <th className="border px-2 py-1 text-left font-semibold">
                        졸업연월
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {edu.map((e, i) => {
                      const degreeStr = e.degree || "";
                      const [levelCode, majorRaw] = degreeStr.split(" - ");
                      const levelLabel =
                        levelCode === "HIGH_SCHOOL"
                          ? t("contact.education.level.highSchool")
                          : levelCode === "UNIVERSITY"
                          ? t("contact.education.level.university")
                          : degreeStr || "-";
                      const major = majorRaw || "";
                      return (
                        <tr key={i}>
                          <td className="border px-2 py-1 align-top">
                            {levelLabel}
                          </td>
                          <td className="border px-2 py-1 align-top">
                            {e.institution || "-"}
                          </td>
                          <td className="border px-2 py-1 align-top">
                            {major || "-"}
                          </td>
                          <td className="border px-2 py-1 align-top">
                            {e.gradDate || "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Row>

          {/* 어학 능력: 표 형태 */}
          <Row label="어학 능력">
            {langs.length === 0 ? (
              "-"
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="border px-2 py-1 text-left font-semibold">
                        시험
                      </th>
                      <th className="border px-2 py-1 text-left font-semibold">
                        언어
                      </th>
                      <th className="border px-2 py-1 text-left font-semibold">
                        점수 / 급수
                      </th>
                      <th className="border px-2 py-1 text-left font-semibold">
                        취득연월
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {langs.map((l, i) => (
                      <tr key={i}>
                        <td className="border px-2 py-1 align-top">
                          {l.exam || "-"}
                        </td>
                        <td className="border px-2 py-1 align-top">
                          {l.lang || "-"}
                        </td>
                        <td className="border px-2 py-1 align-top">
                          {l.score || "-"}
                        </td>
                        <td className="border px-2 py-1 align-top">
                          {l.acquired || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Row>

          {/* 자격 / 면허: 표 형태 */}
          <Row label="자격 / 면허">
            {licenses.length === 0 ? (
              "-"
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="border px-2 py-1 text-left font-semibold">
                        자격 / 면허명
                      </th>
                      <th className="border px-2 py-1 text-left font-semibold">
                        발급기관
                      </th>
                      <th className="border px-2 py-1 text-left font-semibold">
                        취득연월
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {licenses.map((c, i) => (
                      <tr key={i}>
                        <td className="border px-2 py-1 align-top">
                          {c.name || "-"}
                        </td>
                        <td className="border px-2 py-1 align-top">
                          {c.issuer || "-"}
                        </td>
                        <td className="border px-2 py-1 align-top">
                          {c.acquired || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Row>
        </div>

        {/* 5. 첨부 서류 */}
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="border-b px-4 py-3 text-lg font-bold text-blue-900">
            첨부 서류
          </div>

          <Row label="이력서 / 기타 서류">
            {data.resumeUrl ? (
              <div className="flex flex-wrap items-center gap-3">
                <span className="max-w-xs break-all text-xs text-slate-700">
                  {getFileNameFromUrl(data.resumeUrl)}
                </span>
                {/* 화면 전용 다운로드 버튼 (PDF에서는 숨김) */}
                <a
                  href={data.resumeUrl}
                  download
                  target="_blank"
                  rel="noreferrer"
                  className="pdf-hide rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
                >
                  다운로드
                </a>
              </div>
            ) : (
              "미첨부"
            )}
          </Row>
        </div>

        {/* 6. 사전 확인 및 동의 */}
        <div className="pdf-section overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="border-b px-4 py-3 text-lg font-bold text-blue-900">
            사전 확인 및 동의
          </div>

          {/* ✅ 질문 넓게 / 답변 좁게 + YES/NO도 라벨로 */}
          <QARow label={t("contact.elig.q1")}>
            {data.elig1 ? t("contact.yes") : t("contact.no")}
          </QARow>
          <QARow label={t("contact.elig.q2")}>
            {data.elig2 ? t("contact.yes") : t("contact.no")}
          </QARow>
          <QARow label={t("contact.elig.q3")}>
            {data.elig3 ? t("contact.yes") : t("contact.no")}
          </QARow>
          <QARow label={t("contact.elig.q4")}>
            {data.elig4 ? t("contact.yes") : t("contact.no")}
          </QARow>
          <QARow label={t("contact.elig.q5")}>
            {data.elig5 ? t("contact.yes") : t("contact.no")}
          </QARow>
          <QARow label={t("contact.elig.q6")}>
            {data.elig6 ? t("contact.yes") : t("contact.no")}
          </QARow>

          <QARow label="개인정보 수집·이용 동의">
            {data.agree ? "동의" : "미동의"}
          </QARow>
        </div>

        {/* 7. 관리자 메모 (PDF 출력용) */}
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="border-b px-4 py-3 text-lg font-bold text-blue-900">
            담당자 의견
          </div>
          <div className="px-4 py-3 text-sm">
            <pre className="whitespace-pre-wrap break-words text-[13px] leading-relaxed">
              {data.adminMemo || "-"}
            </pre>
          </div>
        </div>
      </div>

      {/* 화면 전용 관리자 메모 입력 박스 (PDF에는 미포함) */}
      <div className="mt-6 rounded-2xl border bg-white p-5 shadow-sm">
        <div className="mb-2 text-sm font-semibold text-slate-700">
          담당자 의견 (지원자 비공개)
        </div>
        <textarea
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          rows={4}
          className="w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="담당자 내부 메모를 입력하세요. (지원자는 볼 수 없습니다)"
        />
        {memoMessage && (
          <div className="mt-2 text-xs text-slate-600">{memoMessage}</div>
        )}
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onSaveMemo}
            disabled={savingMemo}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {savingMemo ? "저장 중..." : "메모 저장"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
